import { Link } from '../../../components/Link';
import {
  Lead,
  LearnList,
  LearnPageLayout,
  LearnSection,
  LearnSubsection,
  LearnTable,
  Tok,
} from './LearnPageLayout';
import { learnArticle } from './learnArticles';

const meta = learnArticle('spam-trigger-words');

/**
 * PILLAR of the copy cluster, and the one page in the section that mostly argues AGAINST the thing
 * its own tool measures. That is deliberate: the honest answer is more useful than a longer word
 * list, and it is the only version of this page that is not already published a thousand times.
 */
export default function SpamTriggerWordsPage() {
  return (
    <LearnPageLayout
      content={{
        meta,
        definition:
          'Spam trigger words are terms said to send mail to the spam folder — free, guarantee, act now. They mattered when filters were mostly keyword rules. Modern filters weigh sender reputation, authentication and recipient engagement far above vocabulary, so word choice is now a small factor with a large reputation.',
        faq: FAQ,
        body: <Body />,
      }}
    />
  );
}

function Body() {
  return (
    <>
      <LearnSection title="Where the lists came from">
        <p>
          Early content filters were largely keyword-driven: a message was scored by matching it
          against a rule set, and words like <Tok>FREE</Tok> and <Tok>Viagra</Tok> carried real
          weight because they were most of what the filter could see.
        </p>
        <p>
          The published "500 spam trigger words" lists date to that period, and most circulating
          today are copies of copies of a handful of originals. They are not maintained against any
          current filter, because there is no current filter they could be maintained against — the
          rules are not public, and they are not primarily lexical any more.
        </p>
        <p>
          What replaced them is a model over sender behaviour. Whether your last thousand messages
          were opened, complained about, or deleted unread tells a filter far more than any word in
          this one does.
        </p>
      </LearnSection>

      <LearnSection title="What actually decides it, in order">
        <LearnTable
          head={['Factor', 'Weight', 'Covered in']}
          rows={[
            ['Authentication passing', <Lead key="a">Gate — failing costs you outright</Lead>,
              <Link key="b" to="/learn/email-authentication" className="text-accent underline underline-offset-4">SPF, DKIM, DMARC</Link>],
            ['Complaint rate', <Lead key="c">Heaviest ongoing signal</Lead>,
              <Link key="d" to="/learn/sender-reputation" className="text-accent underline underline-offset-4">Sender reputation</Link>],
            ['Recipient engagement', 'Very high, especially at Gmail',
              <Link key="e" to="/learn/why-email-goes-to-spam" className="text-accent underline underline-offset-4">Spam placement</Link>],
            ['List quality and bounces', 'High',
              <Link key="f" to="/learn/email-bounce-rate" className="text-accent underline underline-offset-4">Bounce rate</Link>],
            ['Sending pattern and volume', 'Moderate',
              <Link key="g" to="/learn/email-warmup" className="text-accent underline underline-offset-4">Warmup</Link>],
            ['Message structure', 'Low but real', 'Below'],
            ['Individual word choice', <Lead key="h">Lowest of everything here</Lead>, 'Below'],
          ]}
        />
        <p>
          Read the last two rows together with the first. A domain that authenticates cleanly and
          has engaged recipients can write "free" as often as it likes. A domain with a complaint
          problem cannot write its way out of it — and rewriting the copy while the real cause is
          untouched is how people spend a fortnight learning nothing.
        </p>
      </LearnSection>

      <LearnSection title="What still gets flagged">
        <p>
          Structure rather than vocabulary. These are patterns a filter can see without
          understanding a word of your message, which is exactly why they survived:
        </p>
        <LearnList>
          <li>
            <Lead>One large image and almost no text.</Lead> A classic technique for defeating text
            analysis, and still read as one.
          </li>
          <li>
            <Lead>Link shorteners.</Lead> They hide the destination from the filter, which is the
            entire objection. Use your own domain.
          </li>
          <li>
            <Lead>Link domains unrelated to the sending domain.</Lead> Mail from your domain that
            points everywhere except your domain looks like a compromised account.
          </li>
          <li>
            <Lead>Missing or broken unsubscribe.</Lead> Above 5,000 messages a day to Gmail or
            Yahoo, a one-click unsubscribe header is a stated requirement, not a courtesy.
          </li>
          <li>
            <Lead>HTML no mail client would produce.</Lead> Hidden text, white-on-white, absurd
            nesting. All read as attempts to show the filter something different from the reader.
          </li>
          <li>
            <Lead>Sudden style change on an established domain.</Lead> Not about the words
            themselves — a plain-text B2B sender abruptly emitting heavy promotional HTML is a
            change in pattern, and pattern changes get attention.
          </li>
        </LearnList>
        <p>
          Notice none of those is a word. Every one is a property of how the message is built.
        </p>
      </LearnSection>

      <LearnSection title="So why keep a word checker at all?">
        <p>
          A fair question to ask of our own tool, and there are two honest answers.
        </p>
        <LearnSubsection title="Vocabulary is a symptom worth reading">
          <p>
            A subject line stacking <em>free</em>, <em>guarantee</em>, <em>act now</em> and{' '}
            <em>limited time</em> is not going to spam because of those words. It is going to spam
            because that combination is what mail people complain about tends to look like — and
            the complaints are the mechanism. The words are correlated with the outcome without
            causing it, which still makes them a useful thing to notice.
          </p>
        </LearnSubsection>
        <LearnSubsection title="It is the cheapest check you can run">
          <p>
            Thirty seconds, before you send, on copy you have already written. That is a reasonable
            use of a checker. What is not reasonable is running it after placement has already gone
            wrong and treating the output as the diagnosis — at that point the answer is almost
            always further up the table above.
          </p>
        </LearnSubsection>
        <p>
          <Link to="/tools/spam-words-checker" className="text-accent underline underline-offset-4">
            Our checker
          </Link>{' '}
          shows the weighted matches inline, and{' '}
          <Link to="/tools/email-spam-checker" className="text-accent underline underline-offset-4">
            the spam-score tool
          </Link>{' '}
          scores structure alongside vocabulary — which is the more useful half.
        </p>
      </LearnSection>

      <LearnSection title="What to do instead of rewriting">
        <LearnList ordered>
          <li>
            <Lead>Confirm authentication passes.</Lead> Read{' '}
            <Tok>Authentication-Results</Tok> on a message you actually sent. If any of the three is
            not <Tok>pass</Tok>, stop here — you have found it.
          </li>
          <li>
            <Lead>Check your complaint rate.</Lead> Gmail states 0.3% as the ceiling. Above it,
            nothing about your copy matters.
          </li>
          <li>
            <Lead>Suppress disengaged recipients.</Lead> Counter-intuitive and effective: sending to
            fewer, more engaged people raises the rate the filter reads.
          </li>
          <li>
            <Lead>Then look at structure</Lead> — image-to-text ratio, link domains, unsubscribe.
          </li>
          <li>
            <Lead>Then, last, the words.</Lead> And change one thing at a time, or the result tells
            you nothing.
          </li>
        </LearnList>
      </LearnSection>
    </>
  );
}

const FAQ = [
  {
    q: 'Do spam trigger words still matter?',
    a: 'Far less than the published lists suggest. Modern filters weigh authentication, complaint rate and recipient engagement well above vocabulary. Word choice is the smallest factor in a list where the top three are much larger.',
  },
  {
    q: 'Will using the word "free" send my email to spam?',
    a: 'No. Retailers use it constantly and reach the inbox because their authentication passes and their recipients engage. The word is correlated with mail people complain about, and it is the complaints doing the work, not the word.',
  },
  {
    q: 'Where did the spam trigger word lists come from?',
    a: 'Early filters were largely keyword rule sets, so vocabulary genuinely carried weight. The circulating lists date from that period and are mostly copies of a few originals. They are not maintained against any current filter, because current filters are not primarily lexical.',
  },
  {
    q: 'What actually gets a message flagged on content?',
    a: 'Structure rather than words: a single large image with almost no text, link shorteners that hide the destination, link domains unrelated to the sending domain, a missing one-click unsubscribe above 5,000/day, and HTML no mail client would generate.',
  },
  {
    q: 'Should I still run a spam-words checker?',
    a: 'Yes, as a thirty-second pre-send sanity pass — it is cheap and vocabulary is a useful symptom even when it is not a cause. What does not work is running it after placement has gone wrong and treating the output as the diagnosis.',
  },
  {
    q: 'My placement got worse and I did not change the copy. Why?',
    a: 'Because the copy was probably never the variable. Check authentication first, then complaint rate, then whether your list or sending volume changed. Something moved; content that stayed the same is unlikely to be it.',
  },
];
